import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../../components_shared/DataTable';
import { financeAPI } from '../../utils/api';
import Swal from 'sweetalert2';
import { 
  FaFilePdf, FaPlus, FaCheck, FaCoins, FaSearch, 
  FaHourglassHalf, FaCreditCard, FaFileInvoiceDollar, FaTimes 
} from 'react-icons/fa';

export default function VouchersPage() {
  const [rows, setRows] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  // Form State for creating a voucher
  const [form, setForm] = useState({
    invoice_id: '',
    voucher_no: '',
    amount: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Filters State
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [vRes, iRes] = await Promise.all([
        financeAPI.vouchers.getAll(),
        financeAPI.invoices.getAll()
      ]);
      setRows(vRes.data.content || vRes.data || []);
      setInvoices(iRes.data.content || iRes.data || []);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Fetch Error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Filtered rows for visual matrix comparison / search
  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      const matchSearch = !search || 
        r.voucher_no?.toLowerCase().includes(search.toLowerCase()) ||
        String(r.invoice_id).includes(search);
      const matchStatus = !statusFilter || r.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [rows, search, statusFilter]);

  // Invoice options that are processed and eligible for voucher generation
  // (In a production system, invoices with status BILL_PROCESSED have bills approved)
  const eligibleInvoices = useMemo(() => {
    // Filter out invoices that already have a voucher created (matching invoice_id)
    const existingInvoiceIds = rows.map(r => Number(r.invoice_id));
    return invoices.filter(inv => {
      return !existingInvoiceIds.includes(Number(inv.id));
    });
  }, [invoices, rows]);

  // Statistics calculation
  const stats = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter(r => r.status === 'PENDING').length;
    const approved = rows.filter(r => r.status === 'APPROVED').length;
    const paid = rows.filter(r => r.status === 'PAID').length;
    const totalValue = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const disbursed = rows.filter(r => r.status === 'PAID').reduce((sum, r) => sum + Number(r.amount || 0), 0);

    return { total, pending, approved, paid, totalValue, disbursed };
  }, [rows]);

  const act = async (fn, id, confirmText, successText) => {
    const ok = await Swal.fire({
      title: 'Are you sure?',
      text: confirmText,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10ac84',
      cancelButtonColor: '#64748b'
    });
    if (!ok.isConfirmed) return;
    try {
      await fn(id);
      await load();
      Swal.fire({ icon: 'success', text: successText, toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message });
    }
  };

  const handleInvoiceSelect = (e) => {
    const invId = e.target.value;
    const selected = invoices.find(inv => String(inv.id) === invId);
    setForm(prev => ({
      ...prev,
      invoice_id: invId,
      amount: selected ? selected.amount : '',
      voucher_no: `VOUCH-${Date.now().toString().slice(-6)}`
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.invoice_id) errors.invoice_id = 'Invoice is required';
    if (!form.voucher_no.trim()) errors.voucher_no = 'Voucher Number is required';
    if (!form.amount || Number(form.amount) <= 0) errors.amount = 'Valid amount is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateVoucher = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await financeAPI.vouchers.create({
        invoice_id: Number(form.invoice_id),
        voucher_no: form.voucher_no.trim(),
        amount: Number(form.amount),
        status: 'PENDING'
      });
      setModalOpen(false);
      setForm({ invoice_id: '', voucher_no: '', amount: '' });
      await load();
      Swal.fire({ icon: 'success', title: 'Success', text: 'Treasury Voucher generated successfully!' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Creation failed', text: err.response?.data?.error || err.message });
    }
  };

  const nf = new Intl.NumberFormat('en-US');

  const columns = [
    {
      accessorKey: 'voucher_no',
      header: 'Voucher No',
      cell: ({ getValue }) => (
        <code style={{
          fontFamily: "'Courier New', monospace",
          fontSize: '0.85rem', fontWeight: 700,
          background: 'rgba(15,111,173,0.08)', color: '#0f6fad',
          padding: '3px 8px', borderRadius: 5
        }}>
          {getValue()}
        </code>
      )
    },
    {
      accessorKey: 'invoice_id',
      header: 'Invoice ID',
      cell: ({ getValue, row }) => {
        const invId = getValue();
        const inv = invoices.find(i => Number(i.id) === Number(invId));
        return (
          <div>
            <div style={{ fontWeight: 600, color: '#374151' }}>#{invId}</div>
            {inv && <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{inv.invoice_no}</div>}
          </div>
        );
      }
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ getValue }) => (
        <span style={{ fontWeight: 700, color: '#1e293b' }}>
          Rs. {nf.format(getValue() || 0)}
        </span>
      )
    },
    {
      accessorKey: 'created_at',
      header: 'Created Date',
      cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleDateString() : new Date().toLocaleDateString()
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = getValue() || 'PENDING';
        let bg = 'rgba(243,156,18,0.12)';
        let color = '#f39c12';
        if (s === 'APPROVED') { bg = 'rgba(15,111,173,0.12)'; color = '#0f6fad'; }
        if (s === 'PAID') { bg = 'rgba(16,172,132,0.12)'; color = '#10ac84'; }
        return (
          <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: 20,
            fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.5px', background: bg, color
          }}>
            {s}
          </span>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const v = row.original;
        return (
          <div style={{ display: 'flex', gap: 6 }}>
            <button 
              onClick={() => setDetailRow(v)}
              style={{
                background: '#f1f5f9', color: '#475569', border: 'none',
                borderRadius: 6, padding: '5px 10px', fontSize: '0.75rem',
                fontWeight: 600, cursor: 'pointer'
              }}
            >
              Details
            </button>
            {v.status === 'PENDING' && (
              <button 
                onClick={() => act(financeAPI.vouchers.approve, v.id, 'Approve this voucher for disbursement?', 'Voucher approved successfully!')}
                style={{
                  background: 'linear-gradient(135deg, #0f6fad, #0d5a8a)', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '5px 10px', fontSize: '0.75rem',
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                Approve
              </button>
            )}
            {v.status === 'APPROVED' && (
              <button 
                onClick={() => act(financeAPI.vouchers.paid, v.id, 'Confirm disbursement payout for this voucher?', 'Voucher disbursed successfully!')}
                style={{
                  background: 'linear-gradient(135deg, #10ac84, #0e9a75)', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '5px 10px', fontSize: '0.75rem',
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                Disburse
              </button>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        <div style={statCardStyle}>
          <div style={{ ...iconContainerStyle, background: 'rgba(15,111,173,0.1)', color: '#0f6fad' }}>
            <FaFilePdf />
          </div>
          <div>
            <div style={statLabelStyle}>Total Vouchers</div>
            <div style={statValueStyle}>{stats.total}</div>
            <div style={statSubStyle}>Valued: Rs. {nf.format(stats.totalValue)}</div>
          </div>
        </div>
        
        <div style={statCardStyle}>
          <div style={{ ...iconContainerStyle, background: 'rgba(243,156,18,0.1)', color: '#f39c12' }}>
            <FaHourglassHalf />
          </div>
          <div>
            <div style={statLabelStyle}>Pending Approval</div>
            <div style={statValueStyle}>{stats.pending}</div>
            <div style={statSubStyle}>Awaiting audit verification</div>
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={{ ...iconContainerStyle, background: 'rgba(15,111,173,0.1)', color: '#0f6fad' }}>
            <FaCreditCard />
          </div>
          <div>
            <div style={statLabelStyle}>Approved & Open</div>
            <div style={statValueStyle}>{stats.approved}</div>
            <div style={statSubStyle}>Ready for bank release</div>
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={{ ...iconContainerStyle, background: 'rgba(16,172,132,0.1)', color: '#10ac84' }}>
            <FaCoins />
          </div>
          <div>
            <div style={statLabelStyle}>Total Disbursed</div>
            <div style={statValueStyle}>Rs. {nf.format(stats.disbursed)}</div>
            <div style={statSubStyle}>{stats.paid} paid transactions completed</div>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div style={tableCardStyle}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '24px 28px 0', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
              <FaFilePdf style={{ color: '#0f6fad' }} /> Treasury Payment Vouchers
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
              Track cash disbursements and generate bank clearance slips for publisher billing invoices
            </p>
          </div>
          
          <button onClick={() => setModalOpen(true)} style={createBtnStyle}>
            <FaPlus /> Generate Treasury Voucher
          </button>
        </div>

        {/* Filters Panel */}
        <div style={{ display: 'flex', gap: 12, padding: '16px 28px', borderBottom: '1px solid #f0f4f8', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={searchBoxStyle}>
            <FaSearch style={{ color: '#94a3b8', fontSize: '0.8rem', marginRight: 8 }} />
            <input 
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by Voucher ID or Invoice..."
              style={searchInputStyle}
            />
          </div>
          
          <select 
            value={statusFilter} 
            onChange={e => setStatusFilter(e.target.value)} 
            style={selectFilterStyle}
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Pending Approval</option>
            <option value="APPROVED">Approved</option>
            <option value="PAID">Disbursed (Paid)</option>
          </select>
        </div>

        {/* Data Display */}
        <div style={{ padding: '0 28px 24px' }}>
          <DataTable data={filteredRows} columns={columns} loading={loading} />
        </div>
      </div>

      {/* Modal: Generate Voucher */}
      {modalOpen && (
        <div style={modalOverlayStyle} onClick={() => setModalOpen(false)}>
          <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
                <FaFileInvoiceDollar style={{ color: '#10ac84' }} /> Generate Treasury Voucher
              </h3>
              <button onClick={() => setModalOpen(false)} style={closeBtnStyle}><FaTimes /></button>
            </div>
            
            <form onSubmit={handleCreateVoucher} style={{ padding: '24px' }}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Select Eligible Invoice</label>
                <select 
                  value={form.invoice_id} 
                  onChange={handleInvoiceSelect} 
                  style={{ ...inputStyle, borderColor: formErrors.invoice_id ? '#e74c3c' : '#e2e8f0' }}
                >
                  <option value="">-- Select Processed Billing Invoice --</option>
                  {eligibleInvoices.map(inv => (
                    <option key={inv.id} value={inv.id}>
                      Invoice ID #{inv.id} - Ref: {inv.invoice_no || `INV-${inv.id}`} (Rs. {nf.format(inv.amount)})
                    </option>
                  ))}
                </select>
                {formErrors.invoice_id && <span style={errorTextStyle}>{formErrors.invoice_id}</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Treasury Voucher #</label>
                  <input 
                    type="text" 
                    value={form.voucher_no}
                    onChange={e => setForm({ ...form, voucher_no: e.target.value })}
                    placeholder="e.g. VOUCH-0422"
                    style={{ ...inputStyle, borderColor: formErrors.voucher_no ? '#e74c3c' : '#e2e8f0' }}
                  />
                  {formErrors.voucher_no && <span style={errorTextStyle}>{formErrors.voucher_no}</span>}
                </div>

                <div style={fieldStyle}>
                  <label style={labelStyle}>Disbursement Amount (Rs.)</label>
                  <input 
                    type="number" 
                    value={form.amount}
                    onChange={e => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                    style={{ ...inputStyle, borderColor: formErrors.amount ? '#e74c3c' : '#e2e8f0' }}
                  />
                  {formErrors.amount && <span style={errorTextStyle}>{formErrors.amount}</span>}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 28 }}>
                <button type="button" onClick={() => setModalOpen(false)} style={cancelBtnStyle}>Cancel</button>
                <button type="submit" style={submitBtnStyle}>Generate & Authorize</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: View Voucher Details */}
      {detailRow && (
        <div style={modalOverlayStyle} onClick={() => setDetailRow(null)}>
          <div style={{ ...modalContentStyle, maxWidth: 540 }} onClick={e => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>
                Treasury Voucher Information
              </h3>
              <button onClick={() => setDetailRow(null)} style={closeBtnStyle}><FaTimes /></button>
            </div>
            
            <div style={{ padding: '24px' }}>
              <div style={{
                border: '2px dashed #e2e8f0', borderRadius: 12, padding: 24,
                background: '#f8fafc', position: 'relative', overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute', top: 12, right: -28,
                  transform: 'rotate(45deg)', background: detailRow.status === 'PAID' ? '#10ac84' : detailRow.status === 'APPROVED' ? '#0f6fad' : '#f39c12',
                  color: '#fff', fontSize: '0.68rem', fontWeight: 700,
                  padding: '4px 30px', textTransform: 'uppercase', letterSpacing: 0.5
                }}>
                  {detailRow.status}
                </div>
                
                <h4 style={{ margin: '0 0 16px', fontSize: '1.2rem', color: '#0f6fad', borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
                  Sindh Textbook Board ERP
                </h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={infoRowStyle}><span style={infoLabelStyle}>Voucher No:</span><span style={infoValStyle}>{detailRow.voucher_no}</span></div>
                  <div style={infoRowStyle}><span style={infoLabelStyle}>Invoice Ref:</span><span style={infoValStyle}>Invoice ID #{detailRow.invoice_id}</span></div>
                  <div style={infoRowStyle}><span style={infoLabelStyle}>Issue Date:</span><span style={infoValStyle}>{detailRow.created_at ? new Date(detailRow.created_at).toLocaleDateString() : new Date().toLocaleDateString()}</span></div>
                  <div style={infoRowStyle}><span style={infoLabelStyle}>Clearance:</span><span style={infoValStyle}>Treasury Accounts Wing</span></div>
                  <div style={{ ...infoRowStyle, borderTop: '1px solid #e2e8f0', paddingTop: 10, marginTop: 4 }}>
                    <span style={{ ...infoLabelStyle, fontSize: '0.9rem', color: '#1e293b' }}>Payment Sum:</span>
                    <span style={{ ...infoValStyle, fontSize: '1.1rem', fontWeight: 700, color: '#10ac84' }}>Rs. {nf.format(detailRow.amount || 0)}</span>
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                <button type="button" onClick={() => setDetailRow(null)} style={{ ...cancelBtnStyle, width: '100%' }}>Close</button>
                <button type="button" onClick={() => window.print()} style={{ ...submitBtnStyle, width: '100%' }}>Print Copy</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Styling Constants (HSL Tailored ERP style) ---- */
const statCardStyle = {
  background: '#fff', borderRadius: 14, border: '1px solid #f0f4f8',
  padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 18,
  boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
};
const iconContainerStyle = {
  width: 48, height: 48, borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: '1.25rem', flexShrink: 0
};
const statLabelStyle = { fontSize: '0.78rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' };
const statValueStyle = { fontSize: '1.35rem', color: '#1e293b', fontWeight: 700, margin: '2px 0' };
const statSubStyle = { fontSize: '0.72rem', color: '#64748b' };

const tableCardStyle = { background: '#fff', borderRadius: 14, border: '1px solid #f0f4f8', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', overflow: 'hidden' };
const createBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  background: 'linear-gradient(135deg, #10ac84, #0e9a75)', color: '#fff',
  border: 'none', borderRadius: 10, padding: '10px 18px',
  fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(16,172,132,0.2)'
};
const searchBoxStyle = {
  display: 'flex', alignItems: 'center', background: '#f8fafc',
  border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '0 12px',
  height: 40, flex: 1, maxWidth: 380
};
const searchInputStyle = { flex: 1, border: 'none', background: 'transparent', fontSize: '0.85rem', color: '#374151', outline: 'none' };
const selectFilterStyle = {
  padding: '8px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10,
  background: '#f8fafc', fontSize: '0.85rem', color: '#374151',
  cursor: 'pointer', outline: 'none'
};

const modalOverlayStyle = {
  position: 'fixed', inset: 0, zIndex: 90000,
  width: '100vw', height: '100dvh', minHeight: '100vh',
  background: 'rgba(15, 23, 42, 0.58)', backdropFilter: 'blur(5px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  overflow: 'auto'
};
const modalContentStyle = {
  position: 'relative', zIndex: 90001,
  background: '#ffffff', borderRadius: 12, width: 'min(94vw, 580px)', maxWidth: 580,
  maxHeight: 'calc(100dvh - 48px)', overflowY: 'auto',
  border: '1px solid rgba(226, 232, 240, 0.95)',
  boxShadow: '0 30px 90px rgba(15, 23, 42, 0.32), 0 0 0 1px rgba(15, 23, 42, 0.04)',
  color: '#0f172a'
};
const modalHeaderStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '20px 24px', borderBottom: '1px solid #f0f4f8',
  background: '#ffffff'
};
const closeBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.1rem' };

const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 };
const labelStyle = { fontSize: '0.8rem', fontWeight: 600, color: '#475569' };
const inputStyle = {
  padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10,
  background: '#f8fafc', fontSize: '0.88rem', color: '#1e293b', outline: 'none',
  width: '100%', boxSizing: 'border-box'
};
const errorTextStyle = { fontSize: '0.72rem', color: '#e74c3c', marginTop: 3 };

const cancelBtnStyle = {
  flex: 1, padding: '11px', borderRadius: 10, background: '#f1f5f9',
  color: '#475569', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer'
};
const submitBtnStyle = {
  flex: 1, padding: '11px', borderRadius: 10,
  background: 'linear-gradient(135deg, #10ac84, #0e9a75)', color: '#fff',
  border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(16,172,132,0.15)'
};

const infoRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' };
const infoLabelStyle = { fontSize: '0.8rem', color: '#64748b' };
const infoValStyle = { fontSize: '0.85rem', fontWeight: 600, color: '#334155' };
